import { useEffect, useState, useCallback } from "react";
import { useApp } from "@/context/AppContext";
import api, { apiError } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/PageHeader";
import { DatePicker } from "@/components/DatePicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { fmtDate, fmtInt, todayISO } from "@/lib/format";
import { Plus, ClipboardList, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function Inventaires() {
  const { societes, societeParam, societeId } = useApp();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [newDate, setNewDate] = useState(todayISO());
  const [newSoc, setNewSoc] = useState("");
  const [detail, setDetail] = useState(null);
  const [physiques, setPhysiques] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/inventaires", { params: societeParam }); setItems(data.items); }
    catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  }, [societeId]); // eslint-disable-line
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setNewSoc(societeId !== "all" ? societeId : (societes[0]?.id || "")); setNewDate(todayISO()); setOpen(true); };
  const create = async () => {
    if (!newSoc) return toast.error("Société requise.");
    try {
      const { data } = await api.post("/inventaires", { societe_id: newSoc, date: newDate });
      toast.success("Inventaire créé."); setOpen(false); load(); openDetail(data.id);
    } catch (e) { toast.error(apiError(e)); }
  };

  const openDetail = async (id) => {
    const { data } = await api.get(`/inventaires/${id}`);
    setDetail(data);
    setPhysiques(Object.fromEntries(data.lignes.map((l) => [l.product_id, l.stock_physique])));
  };

  const saveSaisie = async () => {
    try {
      const { data } = await api.put(`/inventaires/${detail.id}`, { lignes: Object.entries(physiques).map(([product_id, v]) => ({ product_id, stock_physique: Number(v) || 0 })) });
      setDetail(data); toast.success("Saisie enregistrée."); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const valider = async () => {
    try { const { data } = await api.post(`/inventaires/${detail.id}/valider`); setDetail(data); toast.success("Inventaire validé — stock ajusté."); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <PageHeader title="Inventaires" subtitle="Stock théorique vs physique et validation des écarts" testid="inventaires-page"
        actions={<Button onClick={openNew} className="gap-2" data-testid="new-inv-btn"><Plus className="h-4 w-4" /> Nouvel inventaire</Button>} />

      <div className="rounded-xl border bg-card overflow-hidden">
        {loading ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          : items.length === 0 ? <EmptyState icon={ClipboardList} title="Aucun inventaire" hint="Créez un inventaire pour saisir le stock physique et valider les écarts." />
          : (
            <div className="overflow-x-auto">
              <Table data-testid="inv-table">
                <TableHeader><TableRow className="bg-muted/50"><TableHead>N°</TableHead><TableHead>Date</TableHead><TableHead className="text-right">Produits</TableHead><TableHead>Statut</TableHead><TableHead></TableHead></TableRow></TableHeader>
                <TableBody>
                  {items.map((iv) => (
                    <TableRow key={iv.id} data-testid={`inv-row-${iv.id}`} className="cursor-pointer" onClick={() => openDetail(iv.id)}>
                      <TableCell className="tabular text-sm font-medium">{iv.numero}</TableCell>
                      <TableCell className="tabular text-sm">{fmtDate(iv.date)}</TableCell>
                      <TableCell className="tabular text-right text-sm">{iv.lignes?.length || 0}</TableCell>
                      <TableCell><Badge variant="secondary" className={iv.statut === "valide" ? "bg-success/15 text-success" : "bg-muted"}>{iv.statut === "valide" ? "Validé" : "Brouillon"}</Badge></TableCell>
                      <TableCell className="text-right text-sm text-primary">Ouvrir</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
      </div>

      {/* New */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-popover max-w-sm" data-testid="inv-new-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl">Nouvel inventaire</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5"><Label>Société</Label>
              <Select value={newSoc} onValueChange={setNewSoc}><SelectTrigger data-testid="inv-societe"><SelectValue placeholder="Choisir" /></SelectTrigger><SelectContent className="bg-popover">{societes.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent></Select>
            </div>
            <div className="space-y-1.5"><Label>Date</Label><DatePicker value={newDate} onChange={setNewDate} testid="inv-date" /></div>
            <p className="text-xs text-muted-foreground">L'inventaire capture le stock théorique actuel de tous les produits de la société.</p>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button><Button onClick={create} data-testid="inv-create">Créer</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail / saisie */}
      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="bg-popover max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="inv-detail-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl">Inventaire {detail?.numero} {detail?.statut === "valide" && <Badge className="ml-2 bg-success/15 text-success">Validé</Badge>}</DialogTitle></DialogHeader>
          {detail && (
            <div className="rounded-lg border overflow-hidden">
              <Table>
                <TableHeader><TableRow className="bg-muted/50"><TableHead>Produit</TableHead><TableHead className="text-right">Théorique</TableHead><TableHead className="text-right w-32">Physique</TableHead><TableHead className="text-right">Écart</TableHead></TableRow></TableHeader>
                <TableBody>
                  {detail.lignes.map((l) => {
                    const phys = physiques[l.product_id] ?? l.stock_physique;
                    const ecart = Number(phys) - l.stock_theorique;
                    return (
                      <TableRow key={l.product_id}>
                        <TableCell className="text-sm font-medium">{l.product_name}</TableCell>
                        <TableCell className="tabular text-right text-sm text-muted-foreground">{fmtInt(l.stock_theorique)}</TableCell>
                        <TableCell className="text-right">
                          {detail.statut === "valide" ? <span className="tabular">{fmtInt(l.stock_physique)}</span>
                            : <Input className="h-8 text-right tabular" type="number" value={phys} onChange={(e) => setPhysiques((p) => ({ ...p, [l.product_id]: e.target.value }))} data-testid={`inv-phys-${l.product_id}`} />}
                        </TableCell>
                        <TableCell className={`tabular text-right font-semibold ${ecart > 0 ? "text-success" : ecart < 0 ? "text-destructive" : "text-muted-foreground"}`}>{ecart > 0 ? "+" : ""}{fmtInt(ecart)}</TableCell>
                      </TableRow>
                    );
                  })}
                  {detail.lignes.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-6">Aucun produit dans cette société.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
          )}
          {detail?.statut !== "valide" && (
            <DialogFooter>
              <Button variant="outline" onClick={saveSaisie} data-testid="inv-save">Enregistrer la saisie</Button>
              <Button onClick={valider} className="gap-2" data-testid="inv-valider"><CheckCircle2 className="h-4 w-4" /> Valider et ajuster le stock</Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
